import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { WEEKDAYS_LETTER, addMonths, monthYearLabel, parts, rangeDays, startOfMonth, startOfWeek, weekday, type DateStr } from "../lib/date";
import "./MiniCalendar.css";

interface Props {
  value: DateStr;
  today: DateStr;
  weekStart: number;
  onSelect: (date: DateStr) => void;
  /** Highlight these days as the current selection (e.g. the visible week). */
  highlight?: DateStr[];
  /** Days with events get a small dot. */
  marked?: Set<DateStr>;
  compact?: boolean;
}

/** Month picker (calendar header and date fields). */
export function MiniCalendar({ value, today, weekStart, onSelect, highlight = [], marked, compact }: Props) {
  const [month, setMonth] = useState(startOfMonth(value));
  useEffect(() => setMonth(startOfMonth(value)), [value]);

  const days = rangeDays(startOfWeek(month, weekStart), 42);
  const currentMonth = parts(month).m;
  const selected = new Set(highlight);

  return (
    <div className={`mini-cal${compact ? " compact" : ""}`}>
      <div className="mini-cal-head">
        <span className="mini-cal-title">{monthYearLabel(month)}</span>
        <button type="button" className="icon-btn sm" aria-label="Mês anterior" onClick={() => setMonth(addMonths(month, -1))}>
          <ChevronLeft size={18} />
        </button>
        <button type="button" className="icon-btn sm" aria-label="Próximo mês" onClick={() => setMonth(addMonths(month, 1))}>
          <ChevronRight size={18} />
        </button>
      </div>
      <div className="mini-cal-grid" role="grid">
        {rangeDays(startOfWeek(month, weekStart), 7).map((d) => (
          <span key={`h${d}`} className="mini-cal-dow">
            {WEEKDAYS_LETTER[weekday(d)]}
          </span>
        ))}
        {days.map((d) => {
          const cls = [
            "mini-cal-day",
            parts(d).m !== currentMonth && "other",
            d === today && "today",
            d === value && "selected",
            selected.has(d) && "in-range",
            marked?.has(d) && "marked"
          ]
            .filter(Boolean)
            .join(" ");
          return (
            <button key={d} type="button" className={cls} onClick={() => onSelect(d)} aria-label={d} aria-current={d === today ? "date" : undefined}>
              {parts(d).d}
            </button>
          );
        })}
      </div>
    </div>
  );
}
